import React from 'react';
import {
  Box,
  Paper,
  Typography,
  Avatar,
  Chip,
  Divider,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Button
} from '@mui/material';
import {
  Event as EventIcon,
  LocationOn as LocationIcon,
  AccessTime as TimeIcon,
  Person as PersonIcon,
  Group as GroupIcon,
  Tag as TagIcon
} from '@mui/icons-material';
import { ThreadDetails, ThreadParticipant } from '../../utils/api';
import { getUserInitials } from '../../utils/userUtils';

interface EventDetailsPanelProps {
  threadDetails: ThreadDetails;
  participants: ThreadParticipant[];
  onClose?: () => void;
}

const EventDetailsPanel: React.FC<EventDetailsPanelProps> = ({
  threadDetails,
  participants,
  onClose
}) => {
  const { event, requests } = threadDetails;

  const formatEventTime = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'ACCEPTED': return '#4caf50';
      case 'SUBMITTED': return '#ff9800';
      case 'DECLINED': return '#f44336';
      default: return '#666';
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'ACCEPTED': return 'Confirmed';
      case 'SUBMITTED': return 'Pending';
      case 'DECLINED': return 'Declined';
      default: return status;
    }
  };

  return (
    <Paper 
      elevation={2} 
      sx={{ 
        height: '100%', 
        overflow: 'auto',
        borderRadius: 0,
        borderLeft: '1px solid #e0e0e0'
      }}
    >
      {/* Header */}
      <Box sx={{ p: 3, backgroundColor: '#533B7D', color: 'white' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
          <Avatar sx={{ backgroundColor: 'rgba(255,255,255,0.2)', width: 48, height: 48 }}>
            <EventIcon />
          </Avatar>
          <Box sx={{ flex: 1 }}>
            <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
              Event Details
            </Typography>
            <Typography variant="body2" sx={{ opacity: 0.9 }}>
              {event.activity_type || 'Activity'}
            </Typography>
          </Box>
        </Box>
      </Box>

      <Box sx={{ p: 3 }}>
        {/* Event Title */}
        <Typography variant="h5" sx={{ fontWeight: 'bold', mb: 2, color: '#333' }}>
          {event.title}
        </Typography>

        {/* Event Description */}
        {event.description && (
          <Typography variant="body1" sx={{ mb: 3, color: '#666' }}>
            {event.description}
          </Typography>
        )}

        {/* Event Details */}
        <Box sx={{ mb: 3 }}>
          {/* Time */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
            <TimeIcon sx={{ color: '#533B7D' }} />
            <Box>
              <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                {formatEventTime(event.starts_at)}
              </Typography>
            </Box>
          </Box>

          {/* Location */}
          {(event.address || event.location) && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
              <LocationIcon sx={{ color: '#533B7D' }} />
              <Box>
                <Typography variant="body1">
                  {event.address || event.formatted_address || event.location}
                </Typography>
              </Box>
            </Box>
          )}

          {/* Capacity */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
            <GroupIcon sx={{ color: '#533B7D' }} />
            <Typography variant="body1">
              {participants.length} / {event.capacity} participants
            </Typography>
          </Box>

          {/* Host */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
            <PersonIcon sx={{ color: '#533B7D' }} />
            <Typography variant="body1">
              Hosted by <strong>{event.creator_name}</strong>
            </Typography>
          </Box>
        </Box>

        {/* Tags */}
        {event.tags && event.tags.length > 0 && (
          <Box sx={{ mb: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <TagIcon sx={{ color: '#533B7D', fontSize: 20 }} />
              <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                Tags
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {event.tags.map((tag) => (
                <Chip
                  key={tag.id}
                  label={tag.name}
                  size="small"
                  sx={{
                    backgroundColor: tag.color || '#533B7D',
                    color: 'white',
                    fontSize: '0.8rem'
                  }}
                />
              ))}
            </Box>
          </Box>
        )}

        <Divider sx={{ my: 3 }} />

        {/* Participants */}
        <Box>
          <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 2 }}>
            Participants ({participants.length})
          </Typography>
          
          <List sx={{ p: 0 }}>
            {participants.map((participant) => {
              const request = requests.find(r => r.guest_id === participant.user_id);
              const status = request?.status || 'ACCEPTED';
              
              return (
                <ListItem key={participant.user_id} sx={{ px: 0, py: 1 }}>
                  <ListItemAvatar>
                    <Avatar 
                      sx={{ 
                        backgroundColor: participant.role === 'host' ? '#533B7D' : '#6B46C1',
                        width: 36,
                        height: 36,
                        fontSize: '0.875rem'
                      }}
                    >
                      {getUserInitials(participant.user_name)}
                    </Avatar>
                  </ListItemAvatar>
                  <ListItemText
                    primary={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="body1">
                          {participant.user_name}
                        </Typography>
                        {participant.role === 'host' && (
                          <Chip
                            label="Host"
                            size="small"
                            sx={{
                              backgroundColor: '#533B7D',
                              color: 'white',
                              fontSize: '0.7rem',
                              height: 20
                            }}
                          />
                        )}
                      </Box>
                    }
                    secondary={
                      status !== 'ACCEPTED' && (
                        <Chip
                          label={getStatusLabel(status)}
                          size="small"
                          sx={{
                            backgroundColor: getStatusColor(status),
                            color: 'white',
                            fontSize: '0.7rem',
                            height: 18,
                            mt: 0.5
                          }}
                        />
                      )
                    }
                  />
                </ListItem>
              );
            })}
          </List>
        </Box>
      </Box>
    </Paper>
  );
};

export default EventDetailsPanel;
